import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import {useTranslation} from 'react-i18next';
import Dashboard from '../../components/Dashboard';
import React, {ReactElement, useMemo, useState} from 'react';
import TWithProviders, {
  MRT_ColumnDef_Add_Show,
} from '../../components/MaterialTable';
import {
  createPlatformOrder,
  deleteManyPlatformOrder,
  getPlatformOrderList,
  updatePlatformOrder,
  createPlatformOrderPayment,
} from '../../api/platformOrder';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Alert,
  AlertColor,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  SnackbarOrigin,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {ALERT_HIDE_DURATION} from '../../constant';
import {CreatedAtColumn} from '../../components/Date/CreatedAtColumn';
import {usePlatform} from '../../context/platform';
import {MRT_Cell} from 'material-react-table';
import {usePlatformUsage} from '../../context/platformUsage';
import {StatusColumn} from '../../components/EditTable/ViewTableStatusColumn';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {PlatformUsageDto} from '../../api/dto/platform';
import {UserComponent} from '../../components/EditTable/EditUserColumn';
import {UserInfoCell} from '../../components/User/TabelCell';
dayjs.extend(utc);

// 擴展 Window 接口以包含 zzzpayment
declare global {
  interface Window {
    zzzpayment?: {
      init: (options: {platformId: string; baseUrl?: string}) => void;
      open: (options: {
        platformId?: string;
        amount: number | string;
        currency: string;
        showLoading?: (isLoading: boolean) => void;
        onSuccess?: (result: any) => void;
        onCancel?: () => void;
        onError?: (error: any) => void;
      }) => void;
    };
  }
}

function validate(data: any) {
  const errors: Record<string, string> = {};

  // 確保 amount 是數值型別
  if (data.amount !== undefined) {
    if (isNaN(parseFloat(data.amount))) {
      errors.amount = '必須是有效的數值';
    } else {
      // 確保在提交前將 amount 轉換為數值
      data.amount = parseFloat(data.amount);
    }
  }

  return errors;
}

interface State extends SnackbarOrigin {
  open: boolean;
  type: AlertColor;
  message: string;
  autoHideDuration: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`embed-tabpanel-${index}`}
      aria-labelledby={`embed-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `embed-tab-${index}`,
    'aria-controls': `embed-tabpanel-${index}`,
  };
}

function PlatformOrderContent(): ReactElement {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const {t} = useTranslation();
  const {platformUsage, updatePlatformUsage} = usePlatformUsage();
  const [newOrderAmount, setNewOrderAmount] = useState<number | ''>('');
  const [newOrderCurrency, setNewOrderCurrency] = useState('USDT');
  const [buttonText, setButtonText] = useState('');
  const [showCurrency, setShowCurrency] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [copySuccess, setCopySuccess] = useState({
    button: false,
    iframe: false,
    script: false,
  });
  const {platform} = usePlatform();
  const platformId = platform?.id || '';

  const [state, setState] = React.useState<State>({
    open: false,
    type: 'success',
    message: '',
    vertical: 'bottom',
    horizontal: 'right',
    autoHideDuration: ALERT_HIDE_DURATION,
  });
  const {open} = state;

  React.useEffect(() => {
    setButtonText(t('payNow'));
  }, [t]);

  // 加載支付SDK以供預覽使用
  React.useEffect(() => {
    // 檢查是否已加載SDK
    if (typeof window.zzzpayment !== 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://zusers.zzztech.com.tw/payment-sdk.js';
    script.async = true;
    script.onload = () => {
      if (window.zzzpayment && platformId) {
        window.zzzpayment.init({
          platformId: platformId,
          baseUrl: 'https://zusers.zzztech.com.tw/checkout',
        });
        console.log('Payment SDK loaded for preview');
      }
    };
    document.body.appendChild(script);

    return () => {
      // 清理函數，移除腳本
      const sdkScript = document.querySelector(
        'script[src="https://zusers.zzztech.com.tw/payment-sdk.js"]',
      );
      if (sdkScript) {
        document.body.removeChild(sdkScript);
      }
    };
  }, [platformId]);

  const handleClick = (
    type: AlertColor,
    message: string,
    autoHideDuration?: number,
  ) => {
    setState({
      ...state,
      type,
      message,
      open: true,
      autoHideDuration: autoHideDuration || ALERT_HIDE_DURATION,
    });
  };

  const handleClose = () => {
    setState({...state, open: false});
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const copyToClipboard = (
    text: string,
    type: 'button' | 'iframe' | 'script',
  ) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(prev => ({...prev, [type]: true}));
    setTimeout(() => {
      setCopySuccess(prev => ({...prev, [type]: false}));
    }, 3000);
  };

  // 產生按鈕代碼 - 直接使用前端 API 呼叫
  const getButtonCode = () => {
    return `<!-- 先加載支付 SDK -->
<script src="https://zusers.zzztech.com.tw/payment-sdk.js"></script>

<!-- 初始化支付系統 -->
<script>
  document.addEventListener('DOMContentLoaded', function() {
    window.zzzpayment.init({
      platformId: '${platformId}',
      apiUrl: 'https://zusers.zzztech.com.tw/api'
    });
  });
</script>

<button 
  id="payment-button-example"
  onclick="
    var btn = this;
    btn.disabled = true;
    btn.innerHTML = '處理中...';
    
    window.zzzpayment.open({
      amount: ${newOrderAmount || 0},
      currency: '${newOrderCurrency}',
      showLoading: function(isLoading) {
        if (!isLoading) {
          btn.disabled = false;
          btn.innerHTML = '${buttonText}${
      newOrderAmount ? ` ${newOrderAmount}` : ''
    }${showCurrency ? ` ${newOrderCurrency}` : ''}';
        }
      },
      onError: function(error) {
        console.error('支付錯誤', error);
        btn.disabled = false;
        btn.innerHTML = '${buttonText}${
      newOrderAmount ? ` ${newOrderAmount}` : ''
    }${showCurrency ? ` ${newOrderCurrency}` : ''}';
      }
    });
  "
  style="
    background-color: #1976d2;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-family: Arial, sans-serif;
  "
>
  ${buttonText}${newOrderAmount ? ` ${newOrderAmount}` : ''}${
      showCurrency ? ` ${newOrderCurrency}` : ''
    }
</button>`;
  };

  // 產生 iframe 代碼
  const getIframeCode = () => {
    return `<!-- 先加載支付 SDK -->
<script src="https://zusers.zzztech.com.tw/payment-sdk.js"></script>

<!-- 初始化支付系統 -->
<script>
  document.addEventListener('DOMContentLoaded', function() {
    window.zzzpayment.init({
      platformId: '${platformId}',
      apiUrl: 'https://zusers.zzztech.com.tw/api'
    });
  });
</script>

<!-- iframe 整合方式 -->
<div style="width: 100%;">
  <iframe 
    id="payment-iframe-${platformId}"
    style="display: none; border: none; width: 100%; height: 600px; border-radius: 8px; transition: all 0.3s ease;" 
    allow="payment"
  ></iframe>
  
  <button 
    id="payment-button-${platformId}"
    style="
      background-color: #1976d2;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-family: Arial, sans-serif;
    "
  >
    ${buttonText}${newOrderAmount ? ` ${newOrderAmount}` : ''}${
      showCurrency ? ` ${newOrderCurrency}` : ''
    }
  </button>
  
  <div id="payment-status-${platformId}" style="display: none; margin-top: 10px; padding: 10px; border-radius: 4px; text-align: center;"></div>
</div>

<script>
  // 綁定按鈕點擊事件
  document.getElementById('payment-button-${platformId}').addEventListener('click', function() {
    var button = this;
    var iframe = document.getElementById('payment-iframe-${platformId}');
    var statusDiv = document.getElementById('payment-status-${platformId}');
    
    // 顯示加載中
    button.disabled = true;
    button.innerHTML = '處理中...';
    statusDiv.style.display = 'none';
    
    // 呼叫API建立訂單
    window.zzzpayment.open({
      amount: ${newOrderAmount || 0},
      currency: '${newOrderCurrency}',
      showLoading: function(isLoading) {
        if (!isLoading) {
          button.disabled = false;
          button.innerHTML = '${buttonText}${
      newOrderAmount ? ` ${newOrderAmount}` : ''
    }${showCurrency ? ` ${newOrderCurrency}` : ''}';
        }
      },
      onSuccess: function(result) {
        console.log('付款成功', result);
        // 顯示成功訊息
        statusDiv.textContent = '付款成功！訂單編號: ' + result.orderId;
        statusDiv.style.backgroundColor = '#f0fff0';
        statusDiv.style.border = '1px solid green';
        statusDiv.style.color = 'green';
        statusDiv.style.display = 'block';
      },
      onCancel: function() {
        console.log('付款取消');
        // 顯示取消訊息
        statusDiv.textContent = '付款已取消';
        statusDiv.style.backgroundColor = '#fff0f0';
        statusDiv.style.border = '1px solid #999';
        statusDiv.style.color = '#666';
        statusDiv.style.display = 'block';
      },
      onError: function(error) {
        console.error('付款失敗', error);
        // 顯示錯誤訊息
        statusDiv.textContent = '付款處理時發生錯誤，請稍後再試';
        statusDiv.style.backgroundColor = '#fff0f0';
        statusDiv.style.border = '1px solid #d32f2f';
        statusDiv.style.color = '#d32f2f';
        statusDiv.style.display = 'block';
      }
    });
  });
</script>`;
  };

  // 產生 JavaScript SDK 代碼
  const getScriptCode = () => {
    return `<!-- 在你的HTML中加入以下代碼 -->
<script src="https://zusers.zzztech.com.tw/payment-sdk.js"></script>

<!-- 支付按鈕 -->
<div id="payment-container-${Math.floor(Math.random() * 10000)}">
  <button id="payment-button-${Math.floor(Math.random() * 10000)}">
    ${buttonText}${newOrderAmount ? ` ${newOrderAmount}` : ''}${
      showCurrency ? ` ${newOrderCurrency}` : ''
    }
  </button>
  <div id="payment-status" style="display: none; margin-top: 10px; padding: 10px; border-radius: 4px; text-align: center;"></div>
</div>

<script>
  // 初始化支付 SDK
  document.addEventListener('DOMContentLoaded', function() {
    // 抓取DOM元素
    var container = document.getElementById('payment-container-${Math.floor(
      Math.random() * 10000,
    )}');
    var button = container.querySelector('button');
    var statusDiv = container.querySelector('#payment-status');
    
    // 初始化支付系統
    window.zzzpayment.init({
      platformId: '${platformId}',
      apiUrl: 'https://zusers.zzztech.com.tw/api'
    });
    
    // 綁定按鈕點擊事件
    button.addEventListener('click', function() {
      // 顯示加載中
      button.disabled = true;
      button.innerHTML = '處理中...';
      statusDiv.style.display = 'none';
      
      // 呼叫API建立訂單
      window.zzzpayment.open({
        amount: ${newOrderAmount || 0},
        currency: '${newOrderCurrency}',
        showLoading: function(isLoading) {
          if (!isLoading) {
            button.disabled = false;
            button.innerHTML = '${buttonText}${
      newOrderAmount ? ` ${newOrderAmount}` : ''
    }${showCurrency ? ` ${newOrderCurrency}` : ''}';
          }
        },
        onSuccess: function(result) {
          console.log('付款成功', result);
          // 顯示成功訊息
          statusDiv.textContent = '付款成功！訂單編號: ' + result.orderId;
          statusDiv.style.backgroundColor = '#f0fff0';
          statusDiv.style.border = '1px solid green';
          statusDiv.style.color = 'green';
          statusDiv.style.display = 'block';
        },
        onCancel: function() {
          console.log('付款取消');
          // 顯示取消訊息
          statusDiv.textContent = '付款已取消';
          statusDiv.style.backgroundColor = '#fff0f0';
          statusDiv.style.border = '1px solid #999';
          statusDiv.style.color = '#666';
          statusDiv.style.display = 'block';
        },
        onError: function(error) {
          console.error('付款失敗', error);
          // 顯示錯誤訊息
          statusDiv.textContent = '付款處理時發生錯誤，請稍後再試';
          statusDiv.style.backgroundColor = '#fff0f0';
          statusDiv.style.border = '1px solid #d32f2f';
          statusDiv.style.color = '#d32f2f';
          statusDiv.style.display = 'block';
        }
      });
    });
  });
</script>`;
  };

  const columns = useMemo<MRT_ColumnDef_Add_Show<any>[]>(() => {
    const filteredColumns = [
      {
        accessorKey: 'id',
        header: 'Id',
        enableEditing: false,
        show: false,
        size: 60,
        Cell: ({cell}: {cell: MRT_Cell<any>}) => {
          const [copied, setCopied] = useState(false);
          const idValue = cell.getValue<string>();

          const handleCopy = () => {
            navigator.clipboard.writeText(idValue);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          };

          return (
            <Tooltip
              title={
                copied
                  ? t('dashboardIdCopyTextResult')
                  : t('dashboardIdCopyText')
              }
              arrow
            >
              <IconButton onClick={handleCopy} size='small'>
                <Typography variant='body2' sx={{fontWeight: 800, mr: 1}}>
                  {idValue}
                </Typography>
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'thirdpartyOrderId',
        header: t('thirdpartyOrderIdHeader'),
        enableEditing: false,
        show: false,
        muiEditTextFieldProps: {
          disabled: true,
          error: !!validationErrors?.thirdpartyOrderId,
          helperText: validationErrors?.thirdpartyOrderId,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              thirdpartyOrderId: undefined,
            }),
        },
      },

      {
        accessorKey: 'amount',
        header: t('amountHeader'),
        muiEditTextFieldProps: {
          required: true,
          type: 'number',
          error: !!validationErrors?.amount,
          helperText: validationErrors?.amount,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              amount: undefined,
            }),
        },
        accessorFn: (row: any) => {
          return typeof row.amount === 'string'
            ? parseFloat(row.amount)
            : row.amount;
        },
        Cell: ({cell}: {cell: MRT_Cell<any>}) => {
          const value = cell.getValue();
          return (
            <Typography>
              {value !== undefined && value !== null ? value.toString() : ''}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'status',
        header: t('statusHeader'),
        editVariant: 'select' as any,
        editSelectOptions: [
          {
            label: t('statusFinish'),
            value: 'Finish',
          },
          {label: t('statusFail'), value: 'Fail'},
          {
            label: t('statusPending'),
            value: 'Pending',
          },
        ],
        muiEditTextFieldProps: {
          // required: true,
          error: !!validationErrors?.status,
          helperText: validationErrors?.status,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              status: undefined,
            }),
        },
        Cell: ({cell}: any) => <StatusColumn t={t} cell={cell} />,
        accessorFn: (row: any) => {
          return row.status;
        },
        enableEditing: (row: any) => !!row.original.id,
        muiTableBodyCellEditTextFieldProps: ({row}: {row: any}) => ({
          defaultValue: row.original.status || 'Pending',
        }),
      },
      {
        accessorKey: 'currency',
        header: t('currencyHeader'),
        editVariant: 'select' as any,
        editSelectOptions: [
          {label: 'USDT', value: 'USDT'},
          {label: 'TWD', value: 'TWD'},
          {label: 'USD', value: 'USD'},
        ],
        muiEditTextFieldProps: {
          error: !!validationErrors?.currecy,
          helperText: validationErrors?.currecy,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              currecy: undefined,
            }),
        },
        muiTableBodyCellEditTextFieldProps: ({row}: {row: any}) => ({
          defaultValue: row.original.currecy || 'TWD',
        }),
      },
      {
        accessorKey: 'userInfo',
        header: t('nicknameHeader'),
        enableEditing: true,
        muiEditTextFieldProps: {
          error: !!validationErrors?.nickname || !!validationErrors?.message,
          helperText: validationErrors?.nickname || validationErrors?.message,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              nickname: undefined,
              message: undefined,
            }),
        },
        Edit: ({row}: {row: any}) => (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <TextField
              label={t('nicknameHeader')}
              value={row._valuesCache.nickname || row.original.nickname || ''}
              onChange={e => {
                row._valuesCache.nickname = e.target.value;
              }}
              placeholder={t('enterNickname')}
              fullWidth
            />
            <TextField
              label={t('messageHeader')}
              value={row._valuesCache.message || row.original.message || ''}
              onChange={e => {
                row._valuesCache.message = e.target.value;
              }}
              placeholder={t('enterMessage')}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        ),
        Cell: ({row}: {row: any}) => {
          const nickname = row.original.nickname;
          const message = row.original.message;

          if (!nickname && !message) return null;

          return (
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
              {nickname && (
                <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      mr: 0.5,
                    }}
                  >
                    {nickname}
                  </Typography>
                </Box>
              )}
              {message && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    fontSize: '0.85rem',
                    maxWidth: '200px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {message}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'userId',
        header: t('userHeader'),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.userId,
          helperText: validationErrors?.userId,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              userId: undefined,
            }),
        },
        Edit: UserComponent,
        Cell: ({row}: {row: any}) => (
          <UserInfoCell t={t} user={row.original.user} />
        ),
      },
      {
        accessorKey: 'paymentUrl',
        header: t('paymentUrlHeader'),
        enableEditing: false,
        muiEditTextFieldProps: {
          disabled: true,
          error: !!validationErrors?.paymentUrl,
          helperText: validationErrors?.paymentUrl,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              paymentUrl: undefined,
            }),
        },
        Cell: ({cell}: {cell: MRT_Cell<any>}) => {
          const paymentUrl = cell.getValue() as string;
          if (!paymentUrl) return null;

          return (
            <Tooltip title={paymentUrl} arrow>
              <IconButton
                size='small'
                color='primary'
                onClick={() => window.open(paymentUrl, '_blank')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                <LaunchIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: t('createdAtHeader'),
        enableEditing: false,
        Cell: ({cell}: any) => <CreatedAtColumn cell={cell} />,
      },
    ];

    return filteredColumns;
  }, [t, validationErrors]);

  return (
    <Container
      id='pricing'
      sx={{
        pt: {xs: 4, sm: 12},
        pb: {xs: 8, sm: 16},
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: {xs: 3, sm: 6},
      }}
    >
      <Box
        sx={{
          width: {sm: '100%', md: '60%'},
          textAlign: {sm: 'left', md: 'center'},
          display: 'flex',
          flexDirection: 'column',
          alignItems: {sm: 'flex-start', md: 'center'},
          gap: 1.5,
          py: 2,
        }}
      >
        <Typography
          component='h2'
          variant='h4'
          color='text.primary'
          fontWeight='bold'
        >
          {t('dashboardMenuPlatformOrder')}
        </Typography>
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{maxWidth: '80%', opacity: 0.9}}
        >
          {t('dashboardTablePlatformOrderDescription')}
        </Typography>
      </Box>
      <Tooltip title={t('dashboardTableCreditTooltip')} arrow>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: '-1rem',
          }}
        >
          <Chip
            label={
              <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <Typography variant='body2' color='primary'>
                  {platformUsage?.platformOrderCount}
                </Typography>
                /
                <Typography variant='body2' color='text.secondary'>
                  {platformUsage?.ruleObj.maxLimitPlatformOrderCount}
                </Typography>
              </Box>
            }
            size='small'
            color='default'
            sx={{
              fontSize: '0.75rem',
              height: 24,
              paddingX: 1.2,
              backgroundColor: 'background.paper',
              border: '1px solid #ccc',
            }}
          />
        </Box>
      </Tooltip>

      <TWithProviders
        props={{
          createApi: async (data: any) => {
            if (data.amount !== undefined) {
              data.amount = parseFloat(data.amount);
            }
            data.status = 'Pending';
            const response = await createPlatformOrder(data);
            if (platformUsage) {
              const updatedUsage: PlatformUsageDto = {
                ...platformUsage,
                platformOrderCount: platformUsage.platformOrderCount + 1,
              };
              updatePlatformUsage(updatedUsage);
            }
            return response;
          },
          updateApi: (id: string, data: any) => {
            // 確保 amount 是數值型別
            if (data.amount !== undefined) {
              data.amount = parseFloat(data.amount);
            }
            return updatePlatformOrder(id, data);
          },
          getListApi: getPlatformOrderList,
          // deleteApi: deletePlatformOrder,
          deleteBulkApi: async (ids: string[]) => {
            const response = await deleteManyPlatformOrder(ids);
            if (platformUsage) {
              const updatedUsage: PlatformUsageDto = {
                ...platformUsage,
                platformOrderCount:
                  platformUsage.platformOrderCount - ids.length,
              };
              updatePlatformUsage(updatedUsage);
            }
            return response;
          },
        }}
        columns={columns}
        validate={validate}
        handleClick={handleClick}
        setValidateErrors={setValidationErrors}
      ></TWithProviders>

      {/* 嵌入式支付區塊 */}
      <Paper
        elevation={0}
        variant='outlined'
        sx={{
          width: '100%',
          mt: 4,
          p: 3,
          borderRadius: 2,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant='h5'
          component='h3'
          gutterBottom
          fontWeight='medium'
        >
          {t('embedPaymentSection')}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
          {t('embedPaymentDescription')}
        </Typography>

        <Box sx={{mb: 3}}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label={t('amountHeader')}
              value={newOrderAmount}
              onChange={e => {
                const value = e.target.value;
                if (
                  value === '' ||
                  (!isNaN(Number(value)) && Number(value) > 0)
                ) {
                  setNewOrderAmount(value === '' ? '' : Number(value));
                }
              }}
              type='number'
              InputProps={{
                inputProps: {min: 0.01, step: 0.01},
              }}
              sx={{flex: 1}}
            />
            <TextField
              select
              label={t('currencyHeader')}
              value={newOrderCurrency}
              onChange={e => setNewOrderCurrency(e.target.value)}
              sx={{width: {xs: '100%', sm: '150px'}}}
            >
              <MenuItem value='USDT'>USDT</MenuItem>
            </TextField>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label={t('buttonText')}
              value={buttonText}
              onChange={e => setButtonText(e.target.value)}
              placeholder={t('payNow')}
              sx={{flex: 1}}
              helperText={t('buttonTextHelper')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showCurrency}
                  onChange={e => setShowCurrency(e.target.checked)}
                />
              }
              label={t('showCurrency')}
              sx={{mt: 1}}
            />
          </Box>

          <Typography
            variant='caption'
            color='text.secondary'
            sx={{display: 'block', mt: 1}}
          >
            {t('embedPaymentAmountNote')}
          </Typography>
        </Box>

        <Box sx={{width: '100%', mb: 2}}>
          <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='embed code tabs'
            >
              <Tab label={t('buttonCode')} {...a11yProps(0)} />
              <Tab label={t('iframeCode')} {...a11yProps(1)} />
              <Tab label={t('scriptCode')} {...a11yProps(2)} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant='subtitle2'>
                  {t('buttonCodeDescription')}
                </Typography>
                <Box>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(getButtonCode(), 'button')}
                    color={copySuccess.button ? 'success' : 'primary'}
                    size='small'
                    variant='outlined'
                  >
                    {copySuccess.button ? t('copied') : t('copy')}
                  </Button>
                </Box>
              </Box>

              {/* 預覽 */}
              <Box
                sx={{mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1}}
              >
                <Typography variant='body2' sx={{mb: 1}}>
                  {t('preview')}:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant='contained'
                    color='primary'
                    disabled={!newOrderAmount || !platformId}
                    onClick={() => {
                      const button = document.getElementById('preview-button');
                      if (button) {
                        button.innerHTML = t('loading') || '處理中...';
                        button.setAttribute('disabled', 'true');
                      }

                      // 使用API創建訂單並獲取支付URL
                      if (createPlatformOrderPayment) {
                        createPlatformOrderPayment({
                          platformId,
                          amount: Number(newOrderAmount) || 0,
                          currency: newOrderCurrency,
                          redirectUrl: window.location.href,
                        })
                          .then(response => {
                            if (response?.data?.data?.paymentUrl) {
                              // 開啟支付頁面
                              window.open(
                                response.data.data.paymentUrl,
                                '_blank',
                              );
                            } else {
                              throw new Error('No payment URL returned');
                            }
                          })
                          .catch(error => {
                            console.error('Payment error:', error);
                            handleClick(
                              'error',
                              t('paymentError') ||
                                '獲取支付鏈接時發生錯誤，請稍後再試',
                              5000,
                            );
                          })
                          .finally(() => {
                            if (button) {
                              button.innerHTML = `${buttonText || t('payNow')}${
                                newOrderAmount ? ` ${newOrderAmount}` : ''
                              }${showCurrency ? ` ${newOrderCurrency}` : ''}`;
                              button.removeAttribute('disabled');
                            }
                          });
                      } else {
                        // 如果API函數不存在，顯示錯誤訊息
                        handleClick('error', 'API函數不可用', 5000);
                        if (button) {
                          button.innerHTML = `${buttonText || t('payNow')}${
                            newOrderAmount ? ` ${newOrderAmount}` : ''
                          }${showCurrency ? ` ${newOrderCurrency}` : ''}`;
                          button.removeAttribute('disabled');
                        }
                      }
                    }}
                    id='preview-button'
                  >
                    {buttonText || t('payNow')}
                    {newOrderAmount ? ` ${newOrderAmount}` : ''}
                    {showCurrency ? ` ${newOrderCurrency}` : ''}
                  </Button>
                </Box>
              </Box>

              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {getButtonCode()}
                </pre>
              </Paper>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant='subtitle2'>
                  {t('iframeCodeDescription')}
                </Typography>
                <Box>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(getIframeCode(), 'iframe')}
                    color={copySuccess.iframe ? 'success' : 'primary'}
                    size='small'
                    variant='outlined'
                  >
                    {copySuccess.iframe ? t('copied') : t('copy')}
                  </Button>
                </Box>
              </Box>

              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  overflow: 'auto',
                  maxHeight: '300px',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {getIframeCode()}
                </pre>
              </Paper>

              {/* iframe預覽 */}
              <Box
                sx={{mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1}}
              >
                <Typography variant='body2' sx={{mb: 1}}>
                  {t('preview')}:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <div style={{width: '100%', maxWidth: '500px'}}>
                    <iframe
                      id='preview-iframe'
                      style={{
                        display: 'none',
                        border: 'none',
                        width: '100%',
                        height: '400px',
                        borderRadius: '8px',
                      }}
                      allow='payment'
                    ></iframe>

                    <Button
                      variant='contained'
                      color='primary'
                      id='preview-iframe-button'
                      onClick={() => {
                        const button = document.getElementById(
                          'preview-iframe-button',
                        );
                        const iframe = document.getElementById(
                          'preview-iframe',
                        ) as HTMLIFrameElement;
                        if (button) {
                          button.innerHTML = t('loading') || '處理中...';
                          button.setAttribute('disabled', 'true');
                        }

                        // 使用API創建訂單並獲取支付URL
                        if (createPlatformOrderPayment) {
                          createPlatformOrderPayment({
                            platformId,
                            amount: Number(newOrderAmount) || 0,
                            currency: newOrderCurrency,
                            redirectUrl: window.location.href,
                          })
                            .then(response => {
                              if (response?.data?.data?.paymentUrl && iframe) {
                                // 將iframe的src設為paymentUrl
                                iframe.style.display = 'block';
                                iframe.src = response.data.data.paymentUrl;
                                if (button) button.style.display = 'none';

                                // 監聽支付結果訊息
                                window.addEventListener(
                                  'message',
                                  function handler(event) {
                                    const data = event.data;
                                    if (typeof data === 'object') {
                                      if (
                                        data.status === 'success' ||
                                        data.status === 'cancel'
                                      ) {
                                        iframe.style.display = 'none';
                                        if (button) {
                                          button.style.display = 'block';
                                          button.innerHTML = `${
                                            buttonText || t('payNow')
                                          }${
                                            newOrderAmount
                                              ? ` ${newOrderAmount}`
                                              : ''
                                          }${
                                            showCurrency
                                              ? ` ${newOrderCurrency}`
                                              : ''
                                          }`;
                                          button.removeAttribute('disabled');
                                        }
                                        window.removeEventListener(
                                          'message',
                                          handler,
                                        );
                                      }
                                    }
                                  },
                                );
                              } else {
                                throw new Error('No payment URL returned');
                              }
                            })
                            .catch(error => {
                              console.error('Payment error:', error);
                              handleClick(
                                'error',
                                t('paymentError') ||
                                  '獲取支付鏈接時發生錯誤，請稍後再試',
                                5000,
                              );
                              if (button) {
                                button.innerHTML = `${
                                  buttonText || t('payNow')
                                }${newOrderAmount ? ` ${newOrderAmount}` : ''}${
                                  showCurrency ? ` ${newOrderCurrency}` : ''
                                }`;
                                button.removeAttribute('disabled');
                              }
                            });
                        } else {
                          // 如果API函數不存在，顯示錯誤訊息
                          handleClick('error', 'API函數不可用', 5000);
                          if (button) {
                            button.innerHTML = `${buttonText || t('payNow')}${
                              newOrderAmount ? ` ${newOrderAmount}` : ''
                            }${showCurrency ? ` ${newOrderCurrency}` : ''}`;
                            button.removeAttribute('disabled');
                          }
                        }
                      }}
                      disabled={!newOrderAmount || !platformId}
                    >
                      {buttonText || t('payNow')}
                      {newOrderAmount ? ` ${newOrderAmount}` : ''}
                      {showCurrency ? ` ${newOrderCurrency}` : ''}
                    </Button>
                  </div>
                </Box>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant='subtitle2'>
                  {t('scriptCodeDescription')}
                </Typography>
                <Box>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(getScriptCode(), 'script')}
                    color={copySuccess.script ? 'success' : 'primary'}
                    size='small'
                    variant='outlined'
                  >
                    {copySuccess.script ? t('copied') : t('copy')}
                  </Button>
                </Box>
              </Box>

              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  overflow: 'auto',
                  maxHeight: '400px',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {getScriptCode()}
                </pre>
              </Paper>

              {/* Script預覽 */}
              <Box
                sx={{mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1}}
              >
                <Typography variant='body2' sx={{mb: 1}}>
                  {t('preview')}:
                </Typography>
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                  <Button
                    variant='contained'
                    color='primary'
                    id='script-preview-button'
                    onClick={() => {
                      const button = document.getElementById(
                        'script-preview-button',
                      );
                      const resultDiv =
                        document.getElementById('script-result');

                      if (button) {
                        button.innerHTML = t('loading') || '處理中...';
                        button.setAttribute('disabled', 'true');
                      }

                      if (resultDiv) resultDiv.style.display = 'none';

                      // 使用API創建訂單並獲取支付URL
                      if (createPlatformOrderPayment) {
                        createPlatformOrderPayment({
                          platformId,
                          amount: Number(newOrderAmount) || 0,
                          currency: newOrderCurrency,
                          redirectUrl: window.location.href,
                        })
                          .then(response => {
                            if (response?.data?.data?.paymentUrl) {
                              // 開啟支付頁面
                              window.open(
                                response.data.data.paymentUrl,
                                '_blank',
                              );

                              // 監聽支付結果
                              window.addEventListener(
                                'message',
                                function handler(event) {
                                  const data = event.data;
                                  if (typeof data === 'object') {
                                    if (data.status === 'success') {
                                      if (resultDiv) {
                                        resultDiv.textContent = `付款成功！訂單編號: ${response.data.data.id}`;
                                        resultDiv.className = 'success';
                                        resultDiv.style.display = 'block';
                                      }
                                      window.removeEventListener(
                                        'message',
                                        handler,
                                      );
                                    } else if (data.status === 'cancel') {
                                      if (resultDiv) {
                                        resultDiv.textContent = '付款已取消';
                                        resultDiv.className = 'cancel';
                                        resultDiv.style.display = 'block';
                                      }
                                      window.removeEventListener(
                                        'message',
                                        handler,
                                      );
                                    }
                                  }
                                },
                              );
                            } else {
                              throw new Error('No payment URL returned');
                            }
                          })
                          .catch(error => {
                            console.error('Payment error:', error);
                            handleClick(
                              'error',
                              t('paymentError') ||
                                '獲取支付鏈接時發生錯誤，請稍後再試',
                              5000,
                            );
                            if (resultDiv) {
                              resultDiv.textContent =
                                '付款處理時發生錯誤，請稍後再試';
                              resultDiv.className = 'cancel';
                              resultDiv.style.display = 'block';
                            }
                          })
                          .finally(() => {
                            if (button) {
                              button.innerHTML = `${buttonText || t('payNow')}${
                                newOrderAmount ? ` ${newOrderAmount}` : ''
                              }${showCurrency ? ` ${newOrderCurrency}` : ''}`;
                              button.removeAttribute('disabled');
                            }
                          });
                      } else {
                        // 如果API函數不存在，顯示錯誤訊息
                        handleClick('error', 'API函數不可用', 5000);
                        if (resultDiv) {
                          resultDiv.textContent =
                            '付款處理時發生錯誤，請稍後再試';
                          resultDiv.className = 'cancel';
                          resultDiv.style.display = 'block';
                        }
                      }
                    }}
                    disabled={!newOrderAmount || !platformId}
                  >
                    {buttonText || t('payNow')}
                    {newOrderAmount ? ` ${newOrderAmount}` : ''}
                    {showCurrency ? ` ${newOrderCurrency}` : ''}
                  </Button>
                </Box>

                <div
                  id='script-result'
                  style={{
                    display: 'none',
                    marginTop: '15px',
                    padding: '10px',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                  className=''
                ></div>

                <style>{`
                  #script-result.success {
                    background-color: #f0fff0;
                    border: 1px solid green;
                    color: green;
                  }
                  #script-result.cancel {
                    background-color: #fff0f0;
                    border: 1px solid #999;
                    color: #666;
                  }
                `}</style>
              </Box>
            </Box>
          </TabPanel>
        </Box>

        <Alert severity='info' sx={{mt: 2}}>
          {t('embedPaymentAlert')}
        </Alert>
      </Paper>

      <Snackbar
        anchorOrigin={{vertical: state.vertical, horizontal: state.horizontal}}
        open={open}
        autoHideDuration={ALERT_HIDE_DURATION}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={state.type}
          variant='filled'
          sx={{width: '100%'}}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default function DashboardPlatformOrder() {
  return (
    <>
      <Dashboard element={<PlatformOrderContent />}></Dashboard>
    </>
  );
}
